export default function PrivacyPage() {
  return (
    <div className="bg-white dark:bg-slate-900">
      {/* Hero */}
      <div className="mx-auto max-w-7xl px-6 py-24 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-5xl">
            Privacy Policy
          </h1>
          <p className="mt-6 text-lg text-slate-600 dark:text-slate-300">
            Last Updated: 12 April 2026
          </p>
        </div>
      </div>

      {/* 1. Introduction */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">1. Introduction</h2>
            <div className="mt-6 space-y-4">
              <p className="text-slate-600 dark:text-slate-300">
                CyberActions ("CyberActions", "we", "us", or "our") is a cloud-based cybersecurity
                posture auditing platform designed to analyse security configurations within Microsoft
                365 and Google Workspace environments.
              </p>
              <p className="text-slate-600 dark:text-slate-300">
                We are committed to the principles of Privacy by Design and Default, ensuring that all
                processing activities minimise data collection and protect user confidentiality.
              </p>
              <p className="text-slate-600 dark:text-slate-300">
                This Privacy Policy explains how we collect, use, process, and safeguard personal data
                in accordance with the UK GDPR and the Data Protection Act 2018.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Data Controller & Contact Information */}
      <div className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              2. Data Controller & Contact Information
            </h2>
            <div className="mt-6 space-y-4">
              <p className="text-slate-600 dark:text-slate-300">CyberActions acts as:</p>
              <ul className="list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
                <li>
                  <span className="font-semibold text-slate-900 dark:text-white">Data Controller</span>{' '}
                  for account, billing, and website-related data
                </li>
                <li>
                  <span className="font-semibold text-slate-900 dark:text-white">Data Processor</span>{' '}
                  when analysing customer security configuration metadata on behalf of organisations
                </li>
              </ul>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Legal Entity</h3>
                <p className="mt-2 text-slate-600 dark:text-slate-300">
                  CyberActions Ltd (registered in England and Wales)
                </p>
                <p className="text-slate-600 dark:text-slate-300">
                  Company Number: [Insert Company Number]
                </p>
                <p className="text-slate-600 dark:text-slate-300">
                  Registered Office: [Insert Address]
                </p>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Contact Details
                </h3>
                <p className="mt-2 text-slate-600 dark:text-slate-300">
                  Email:{' '}
                  <a
                    href="mailto:privacy@cyberactions.co.uk"
                    className="text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    privacy@cyberactions.co.uk
                  </a>
                </p>
                <p className="text-slate-600 dark:text-slate-300">
                  Data Protection Lead: The Privacy Team
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 3. The "No Content" Guarantee */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              3. The &ldquo;No Content&rdquo; Guarantee
            </h2>
            <div className="mt-6 space-y-4">
              <p className="text-slate-600 dark:text-slate-300">
                CyberActions is designed with strict data minimisation principles.
              </p>
              <p className="text-slate-600 dark:text-slate-300">
                We do not access, collect, or store:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
                <li>The contents of emails or attachments</li>
                <li>Files, documents, or stored data within OneDrive or Google Drive</li>
                <li>Calendar data or private communications (e.g., Teams, Slack)</li>
                <li>User passwords or authentication credentials</li>
              </ul>
              <p className="text-slate-600 dark:text-slate-300">
                We only process security configuration metadata, not user-generated content.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 4. Information We Collect */}
      <div className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              4. Information We Collect
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              We collect and process the following categories of data:
            </p>

            {/* 4.1 */}
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                4.1 Account Information (Controller)
              </h3>
              <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
                <li>Name</li>
                <li>Work email address</li>
                <li>Organisation name</li>
                <li>Job title</li>
              </ul>
              <p className="mt-4 text-slate-600 dark:text-slate-300">
                <span className="font-semibold text-slate-900 dark:text-white">Purpose:</span>{' '}
                Account creation, authentication, billing, and service communication.
              </p>
            </div>

            {/* 4.2 */}
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                4.2 Platform Usage & Technical Data
              </h3>
              <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
                <li>IP address</li>
                <li>Browser type and device information</li>
                <li>Login activity and session logs</li>
              </ul>
              <p className="mt-4 text-slate-600 dark:text-slate-300">
                <span className="font-semibold text-slate-900 dark:text-white">Purpose:</span>
              </p>
              <ul className="mt-2 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
                <li>Platform security and monitoring</li>
                <li>Fraud prevention</li>
                <li>Performance optimisation</li>
              </ul>
            </div>

            {/* 4.3 */}
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                4.3 Security Configuration Metadata (Processor)
              </h3>
              <p className="mt-4 text-slate-600 dark:text-slate-300">
                This is the core data used to deliver the Service:
              </p>
              <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
                <li>Multi-Factor Authentication (MFA) status</li>
                <li>User roles and administrative privileges</li>
                <li>Device compliance status (e.g., encryption, OS version)</li>
                <li>External sharing settings (OneDrive / Google Drive)</li>
                <li>Security alerts and risk indicators</li>
              </ul>
              <p className="mt-4 font-semibold text-slate-900 dark:text-white">
                Important:{' '}
                <span className="font-normal text-slate-600 dark:text-slate-300">
                  This data does not include file contents, emails, or personal communications.
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 5. Lawful Basis for Processing */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              5. Lawful Basis for Processing
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              We process personal data under the following legal bases:
            </p>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>
                <span className="font-semibold text-slate-900 dark:text-white">
                  Contractual Necessity
                </span>{' '}
                &ndash; to provide the Service
              </li>
              <li>
                <span className="font-semibold text-slate-900 dark:text-white">
                  Legitimate Interests
                </span>{' '}
                &ndash; to improve security, detect fraud, and enhance functionality
              </li>
              <li>
                <span className="font-semibold text-slate-900 dark:text-white">
                  Legal Obligation
                </span>{' '}
                &ndash; to comply with UK legal and regulatory requirements
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* 6. Third-Party Integrations (OAuth Access) */}
      <div className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              6. Third-Party Integrations (OAuth Access)
            </h2>
            <div className="mt-6 space-y-4">
              <p className="text-slate-600 dark:text-slate-300">CyberActions connects to:</p>
              <ul className="list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
                <li>Microsoft 365</li>
                <li>Google Workspace</li>
              </ul>
              <p className="text-slate-600 dark:text-slate-300">
                via secure OAuth 2.0 authentication.
              </p>
              <p className="text-slate-600 dark:text-slate-300">
                <span className="font-semibold text-slate-900 dark:text-white">Key Principles:</span>
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
                <li>Read-Only Access Only</li>
                <li>No password collection or storage</li>
                <li>User-controlled access (revocable at any time)</li>
              </ul>
              <p className="text-slate-600 dark:text-slate-300">
                You can revoke access instantly through your Microsoft or Google Admin settings.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 7. Data Minimisation */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              7. Data Minimisation
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              We adhere strictly to data minimisation principles:
            </p>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>Only collect data necessary to perform security audits</li>
              <li>Avoid accessing user-generated content</li>
              <li>Limit data processing to configuration-level metadata</li>
            </ul>
          </div>
        </div>
      </div>

      {/* 8. Data Retention */}
      <div className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">8. Data Retention</h2>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>Data is retained for the duration of your active subscription</li>
              <li>Upon account termination, data is deleted or anonymised within 30 days</li>
              <li>
                Certain data may be retained where required for legal or regulatory purposes
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* 9. Data Storage & Location */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              9. Data Storage & Location
            </h2>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>All primary data is hosted within the United Kingdom</li>
              <li>Secure cloud infrastructure is used with strict access controls</li>
            </ul>
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Backups</h3>
              <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
                <li>Encrypted backups are maintained for disaster recovery</li>
                <li>Backup data is rotated and securely deleted on a defined schedule</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* 10. International Data Transfers */}
      <div className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              10. International Data Transfers
            </h2>
            <div className="mt-6 space-y-4">
              <p className="text-slate-600 dark:text-slate-300">
                Some third-party providers (e.g., cloud platforms or APIs) may process data outside
                the United Kingdom.
              </p>
              <p className="text-slate-600 dark:text-slate-300">
                Where this occurs, CyberActions ensures appropriate safeguards are in place,
                including:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
                <li>Standard Contractual Clauses (SCCs)</li>
                <li>UK-approved international data transfer mechanisms</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* 11. Subprocessors */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">11. Subprocessors</h2>
            <div className="mt-6 space-y-4">
              <p className="text-slate-600 dark:text-slate-300">
                We may engage trusted third-party service providers (subprocessors) to support our
                operations, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
                <li>Cloud hosting providers</li>
                <li>Email delivery services</li>
                <li>Monitoring and analytics tools</li>
              </ul>
              <p className="text-slate-600 dark:text-slate-300">All subprocessors are:</p>
              <ul className="list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
                <li>Contractually bound to data protection obligations</li>
                <li>Restricted to processing data only as instructed</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* 12. Security Measures */}
      <div className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              12. Security Measures
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              We implement appropriate technical and organisational measures, including:
            </p>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>Encryption in transit (TLS)</li>
              <li>Encryption at rest</li>
              <li>Role-based access controls (least privilege)</li>
              <li>Secure API authentication (OAuth 2.0)</li>
              <li>Continuous monitoring and audit logging</li>
            </ul>
          </div>
        </div>
      </div>

      {/* 13. Automated Decision-Making */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              13. Automated Decision-Making
            </h2>
            <div className="mt-6 space-y-4">
              <p className="text-slate-600 dark:text-slate-300">
                CyberActions does not perform automated decision-making that produces legal or
                similarly significant effects.
              </p>
              <p className="text-slate-600 dark:text-slate-300">
                All outputs are advisory and require human interpretation.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 14. Your Rights (UK GDPR) */}
      <div className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              14. Your Rights (UK GDPR)
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              Under the UK GDPR, you have the right to:
            </p>
            <ul className="mt-4 list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion (&ldquo;right to be forgotten&rdquo;)</li>
              <li>Restrict or object to processing</li>
              <li>Request data portability</li>
            </ul>
            <div className="mt-6">
              <p className="text-slate-600 dark:text-slate-300">
                To exercise your rights, contact:{' '}
                <a
                  href="mailto:privacy@cyberactions.co.uk"
                  className="text-primary-600 dark:text-primary-400 hover:underline"
                >
                  privacy@cyberactions.co.uk
                </a>
              </p>
              <p className="mt-2 text-slate-600 dark:text-slate-300">
                We respond within 30 days.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 15. Complaints */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">15. Complaints</h2>
            <div className="mt-6 space-y-4">
              <p className="text-slate-600 dark:text-slate-300">
                If you believe your data has been handled improperly, you have the right to lodge a
                complaint with the:
              </p>
              <p className="text-slate-600 dark:text-slate-300">
                <span className="font-semibold text-slate-900 dark:text-white">
                  Information Commissioner&apos;s Office (ICO)
                </span>
                <br />
                Website:{' '}
                <a
                  href="https://ico.org.uk"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 dark:text-primary-400 hover:underline"
                >
                  https://ico.org.uk
                </a>
              </p>
              <p className="text-slate-600 dark:text-slate-300">
                We encourage you to contact us first to resolve any concerns.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 16. Children's Privacy */}
      <div className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              16. Children&apos;s Privacy
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300">
              The Service is not intended for individuals under the age of 18, and we do not
              knowingly collect personal data from children.
            </p>
          </div>
        </div>
      </div>

      {/* 17. Changes to This Policy */}
      <div className="bg-slate-50 dark:bg-slate-800/50 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              17. Changes to This Policy
            </h2>
            <div className="mt-6 space-y-4">
              <p className="text-slate-600 dark:text-slate-300">
                We may update this Privacy Policy from time to time.
              </p>
              <p className="text-slate-600 dark:text-slate-300">
                Material changes will be communicated via:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-600 dark:text-slate-300">
                <li>Email notification</li>
                <li>Platform alerts</li>
              </ul>
              <p className="text-slate-600 dark:text-slate-300">
                Continued use of the Service constitutes acceptance of the updated policy.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
